// @flow

import React from "react";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { Button, Col, Row, type StepProps, SchemaForm, View } from "@components";
import type {
  ReferralManagerSettings,
  ReferralReason,
  ReferralKpi,
} from "@modules/service/reducer/referrals/types";

import {
  CommonReasonInputForm,
  RtwFormForm,
  CommonComplianceKpisForm,
} from "@modules/service/components/";
import { removeNullAndUndefinedProps } from "@transformers/object";

type OwnProps = $Exact<{
  editing: boolean,
  schema: Object,
  nextButtonLabel: string,
  data: ReferralManagerSettings,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepManagerReferrals extends React.Component<Props> {
  form: *;
  validator: *;
  errorMessage: string | boolean;

  get data() {
    return {
      ...this.props.data,
      referralMechanism: "ONLINE_FORM",
    };
  }
  get schema() {
    return this.props.schema || {};
  }
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }
  componentDidMount() {
    this.validator = new Ajv().compile(this.schema);
  }
  isValid = () => {
    if (this.validator) {
      let isValid = this.validator(
        removeNullAndUndefinedProps({
          ...this.data,
        }),
      );

      this.errorMessage = ((this.validator.errors || [])[0] || {}).message || false;

      if ((this.data.declinedReasons || []).filter(reason => reason.isActive === true).length < 1) {
        isValid = false;
        this.errorMessage = this.props.t("AT_LEAST_ONE_ACTIVE_REASON_MUST_BE_PROVIDED");
      }

      return isValid;
    }
    return false;
  };

  submitForm = () => {
    this.formRef.current.submit();
  };
  updateAlertConfigurations = (formData: Object) => {
    const allIds = [];
    const alertConfigurations =
      formData &&
      formData.filter &&
      formData.filter((item: { id: number }) => {
        const isIn = allIds.indexOf(item.id);
        if (isIn === -1) {
          allIds.push(item.id);
        }
        return isIn === -1;
      });
    this.props.onChange({ ...this.data, alertConfigurations: alertConfigurations }, true);
  };

  updateDeclinedReasons = (declinedReasons: Array<ReferralReason>) => {
    this.props.onChange({ ...this.data, declinedReasons }, true);
  };
  updateFormQuestions = (formQuestions: Array<Object>) => {
    this.props.onChange({ ...this.data, formQuestions }, true);
  };
  updateKpis = (kpis: Array<ReferralKpi>) => {
    this.props.onChange({ ...this.data, kpis }, true);
  };

  submitData = () => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(this.data, true);
    }
  };
  render() {
    if (!this.props.editable) {
      return <div />;
    }
    const { alertConfigurations, declinedReasons, formQuestions, kpis } = this.schema.properties;

    return (
      <React.Fragment>
        {/*Alerts for Manager Led Referrals*/}
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12" className="mb-4">
            <h6>{this.props.t("ALERTS_FOR_MANAGER_LED_REFERRALS")}</h6>
          </Col>
          <Col sm="12">
            {this.schema && this.data ? (
              <SchemaForm
                ref={this.formRef}
                required={true}
                dontTransformTheData
                formData={this.data.alertConfigurations}
                data={this.data.alertConfigurations}
                schema={alertConfigurations}
                uiSchema={{
                  "ui:options": {
                    addButtonTitle: this.props.t("ADD_ALERT"),
                  },
                }}
                onChange={this.updateAlertConfigurations}
                onSubmit={this.submitData}
                externalSubmit
              />
            ) : null}
          </Col>
        </Row>
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12" className="mb-4">
            <h6>{this.props.t("DECLINED_REFERRAL")}</h6>
          </Col>

          <Col sm="12">
            {this.schema && this.data ? (
              <CommonReasonInputForm
                data={this.data.declinedReasons}
                schema={declinedReasons.items}
                onChange={this.updateDeclinedReasons}
              />
            ) : null}
          </Col>
        </Row>
        {/* Referral Mechanism */}
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12" className="mb-4">
            <h6>{this.props.t("REFERRAL_DETAILS_REFERRAL_MECHANISM")}</h6>
          </Col>
          <Col sm="12">
            <RtwFormForm
              data={this.data.formQuestions}
              onChange={this.updateFormQuestions}
              schema={formQuestions.items}
            />
          </Col>
        </Row>

        {/*Compliance Settings*/}
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12" className="mb-4">
            <h6>{this.props.t("COMPLIANCE_SETTINGS")}</h6>
          </Col>

          <Col sm="12">
            {this.schema && this.data ? (
              <CommonComplianceKpisForm
                data={this.data.kpis}
                schema={kpis.items}
                onChange={this.updateKpis}
                kpiTitle={this.props.t("KPI_TARGET_HOURS")}
              />
            ) : null}
          </Col>
        </Row>
        {!this.props.isCollapsable && (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button onClick={this.submitForm} disabled={!this.isValid()}>
              {this.props.nextButtonLabel}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

export default localize()(StepManagerReferrals);
